import { useSyncExternalStore } from "react";
import type { Task } from "@/hooks/useTasks";

export type TaskChange = { userId: string; id: string; task: Task | null } | { userId: string; refresh: true };
const listeners = new Set<(change: TaskChange) => void>();
const revisions = new Map<string, number>();
export const taskRevision = (userId: string) => revisions.get(userId) ?? 0;
export function subscribeTaskChanges(listener: (change: TaskChange) => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function publishTaskChange(change: TaskChange) {
  revisions.set(change.userId, taskRevision(change.userId) + 1);
  listeners.forEach(listener => listener(change));
}
// Recovery can confirm a durable receipt without possessing its old row.
// Mounted views fetch current owned state instead of replaying stale metadata.
export function publishTaskRefresh(userId: string) {
  publishTaskChange({ userId, refresh: true });
}

// Shared across the drawer and weekly card: one write per task at a time.
let pending: ReadonlySet<string> = new Set();
const pendingListeners = new Set<() => void>();
const subscribePending = (listener: () => void) => {
  pendingListeners.add(listener);
  return () => { pendingListeners.delete(listener); };
};
const snapshot = () => pending;
export const usePendingTasks = () => useSyncExternalStore(subscribePending, snapshot, snapshot);
export async function withTaskWrite<T>(id: string, write: () => Promise<T>): Promise<T> {
  if (pending.has(id)) throw new Error("TASK_WRITE_PENDING");
  pending = new Set([...pending, id]);
  pendingListeners.forEach(listener => listener());
  try { return await write(); }
  finally {
    pending = new Set([...pending].filter(key => key !== id));
    pendingListeners.forEach(listener => listener());
  }
}
